import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  CircularProgress,
  Button
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { fetchWithAuth } from '../utils/fetchWithAuth';

// PayPal config constants (later move to env/config endpoint)
const PAYPAL_CLIENT_ID =
  'BAAwb_c_SoD0vgJSVGqyr1Ap14xk5J8Dl7K4S8e18NEinfrODkuqwMqL-TsG8YwDjGQ0yf3wv51Mc29mNQ';
const HOSTED_BUTTON_ID = 'TGF3HE76EEYMY';

function loadPayPalSdk() {
  if (window.paypal) return Promise.resolve();

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src =
      `https://www.paypal.com/sdk/js?client-id=${PAYPAL_CLIENT_ID}&components=hosted-buttons&enable-funding=venmo&currency=USD`;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load PayPal SDK'));
    document.head.appendChild(script);
  });
}

const PaymentDialog = ({ open, onClose }) => {
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const formatUSD = (value) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(value || 0));

  const fetchBalance = async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await fetchWithAuth('/api/users/balance');
      if (!resp.ok) throw new Error(`Status: ${resp.status}`);
      const data = await resp.json();
      const amount = Number(data?.balance ?? 0);
      setBalance(amount);
      // Dev observability (no PII)
      // eslint-disable-next-line no-console
      console.log(`[PAYMENT_UI] balance_loaded amount=${amount.toFixed(2)}`);
    } catch (e) {
      setError('Failed to load balance');
    } finally {
      setLoading(false);
    }
  };

  // fetch balance each time dialog opens
  useEffect(() => {
    if (!open) return;
    // eslint-disable-next-line no-console
    console.log('[PAYMENT_UI] dialog_opened');
    (async () => {
      await fetchBalance();
    })();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    loadPayPalSdk()
      .then(() => {
        if (cancelled) return;
        if (window.paypal && window.paypal.HostedButtons) {
          try {
            window.paypal
              .HostedButtons({
                hostedButtonId: HOSTED_BUTTON_ID,
                onApprove: (data) => {
                  // Redirect to success page with PayPal payment/order ID so backend can verify
                  const paymentId =
                    data.orderID || data.paymentID || data.id || data.token;
                  console.log('Payment approved, redirecting with paymentId:', paymentId);
                  window.location.href = `/payment/success?paymentId=${encodeURIComponent(
                    paymentId
                  )}`;
                },
                onCancel: () => {
                  // Handle payment cancellation
                  console.log('Payment cancelled');
                  // Keep the dialog open
                }
              })
              .render('#paypal-container-TGF3HE76EEYMY');
          } catch (err) {
            // eslint-disable-next-line no-console
            console.error('PayPal render error:', err);
          }
        }
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error(err);
      });
    return () => {
      cancelled = true;
    };
  }, [open, onClose]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ m: 0, p: 2 }}>
        Payment
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box mb={2} textAlign="center">
            <CircularProgress size={24} />
          </Box>
        ) : error ? (
          <Box mb={2} textAlign="center">
            <Typography color="error" variant="body2" gutterBottom>
              {error}
            </Typography>
            <Button variant="outlined" size="small" onClick={fetchBalance}>
              Retry
            </Button>
          </Box>
        ) : balance !== null ? (
          <Box mb={2}>
            <Typography variant="body2">
              Current balance: {formatUSD(balance)}
            </Typography>
          </Box>
        ) : null}
        <Box textAlign="center">
          <div id="paypal-container-TGF3HE76EEYMY" />
        </Box>
      </DialogContent>
    </Dialog>
  );
};

PaymentDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default PaymentDialog;
